import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Box, BoxProps, chakra } from '@chakra-ui/react';

import { ItemTypes } from './types';

export interface DragBoxProps extends BoxProps {
  id: any;
  left?: number;
  top?: number;
  text: string;
  noDrag?: boolean;
  onKeyboardSelect?: () => void;
  isKeyboardSelected?: boolean;
}

const DragBox: React.FC<DragBoxProps> = ({
  id,
  left,
  top,
  text,
  noDrag,
  onKeyboardSelect,
  isKeyboardSelected,
  ...rest
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.DragBox,
      canDrag: !noDrag,
      item: () => ({
        id,
        left,
        top,
        width,
        height,
        el: elementRef.current,
        text,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, left, top, elementRef.current, noDrag],
  );

  const attach = useCallback(
    (el) => {
      drag(el);
      elementRef.current = el;
    },
    [drag],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      setWidth(elementRef.current.offsetWidth);
      setHeight(elementRef.current.offsetHeight);
    }
  }, [elementRef]);

  return (
    <chakra.div
      opacity={isDragging ? 0 : undefined}
      color="blue.500"
      position={left != null || top != null ? 'absolute' : 'relative'}
      cursor={noDrag ? 'default' : 'move'}
      left={left}
      top={top}
      ref={attach}
      tabIndex={onKeyboardSelect && !noDrag ? 0 : undefined}
      role={onKeyboardSelect ? 'button' : undefined}
      aria-pressed={onKeyboardSelect ? isKeyboardSelected : undefined}
      background={isKeyboardSelected ? 'rgba(34, 86, 255, 0.1)' : undefined}
      boxShadow={isKeyboardSelected ? '0 0 0 3px #2256ff' : undefined}
      borderRadius="8px"
      sx={{
        '&:focus-visible': {
          outline: '3px solid var(--stemi-focus-color)',
          outlineOffset: '3px',
          borderRadius: '8px',
        },
      }}
      onKeyDown={onKeyboardSelect ? (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onKeyboardSelect();
        }
      } : undefined}
    >
      <DragBoxPreview text={text} {...rest} />
    </chakra.div>
  );
};

const DragBoxPreview: React.FC<Omit<DragBoxProps, 'id'>> = ({
  text,
  ...rest
}) => {
  return (
    <Box
      color="blue.500"
      borderRadius="8px"
      borderColor="blue.500"
      backgroundColor="white"
      border="1px"
      fontWeight="bold"
      fontSize="sm"
      px={2}
      py={1}
      w="fit-content"
      transition="all 250ms ease-in-out"
      boxShadow="md"
      {...rest}
    >
      {text}
    </Box>
  );
};

export { DragBox, DragBoxPreview };
export default DragBox;
